import { useEffect, useState } from 'react';

import type { DrawingManager } from '@cadstrom/renderer';

export interface CanvasPoint {
	x: number;
	y: number;
}

export interface CanvasAnchor {
	/** Client (viewport) coordinates of the press that created the anchor. */
	position: CanvasPoint;
	/** The same point in drawing space, which the viewport pans and zooms around. */
	world: CanvasPoint;
}

interface TrackedAnchor {
	anchor: CanvasAnchor | null;
	position: CanvasPoint | null;
}

/**
 * Tracks a drawing-space point's client position as the renderer viewport pans and zooms, whether
 * by pointer, wheel, or programmatic zoom.
 *
 * A new anchor starts at its own press position and is re-projected every animation frame while
 * it is tracked; frames on which nothing moved cause no update.
 *
 * @param drawingManager - The canvas's drawing manager, or `null` before one exists.
 * @param canvasArea - The element the canvas fills, which maps canvas to client coordinates.
 * @param anchor - The point to track; a new object starts tracking a new point.
 * @returns The point's current client position, or `null` when there is nothing to track or the
 *   point has left the canvas area.
 */
export function useCanvasAnchoredPosition(
	drawingManager: DrawingManager | null,
	canvasArea: HTMLElement | null,
	anchor: CanvasAnchor | null,
): CanvasPoint | null {
	const [tracked, setTracked] = useState<TrackedAnchor>({ anchor, position: anchor?.position ?? null });
	// A new anchor is shown at its press position until the first projection; reset during render.
	if (tracked.anchor !== anchor) {
		setTracked({ anchor, position: anchor?.position ?? null });
	}

	useEffect(() => {
		if (anchor === null || canvasArea === null || drawingManager === null || drawingManager.isDestroyed) {
			return;
		}

		const project = () => {
			const rect = canvasArea.getBoundingClientRect();
			const local = drawingManager.renderer.getViewport().toScreen(anchor.world.x, anchor.world.y);
			const inside = local.x >= 0 && local.y >= 0 && local.x <= rect.width && local.y <= rect.height;
			const position = inside ? { x: rect.left + local.x, y: rect.top + local.y } : null;
			setTracked((current) => {
				if (current.anchor !== anchor) {
					return current;
				}
				const unchanged =
					current.position === position ||
					(current.position !== null &&
						position !== null &&
						current.position.x === position.x &&
						current.position.y === position.y);
				return unchanged ? current : { anchor, position };
			});
		};

		// Programmatic zooms emit no viewport event, so poll the projection once per frame instead.
		let rafId = requestAnimationFrame(function tick() {
			if (drawingManager.isDestroyed) {
				return;
			}
			project();
			rafId = requestAnimationFrame(tick);
		});
		return () => cancelAnimationFrame(rafId);
	}, [anchor, canvasArea, drawingManager]);

	return tracked.anchor === anchor ? tracked.position : (anchor?.position ?? null);
}
